import { delegate } from "@otto-ec/global-resources/event";
import { type DataTrLifeCycleEventMap, unlockInit } from "@otto-ec/global-resources/tracking";
import type { DataContainer, Feature } from "@otto-ec/tracking-bct";
import { DealBlockNavigation } from "./dealblock";
import { getClosestParentByDataAttribute, getElementPositionInPageContext, removeQuoteEncoding } from "../utils";

const addPositionToFeatures = (position: number, features?: Feature[]) => {
	if (!features?.length) return;

	features[0]!.position = position;
};

const addFeatureOrder = (ev: DataTrLifeCycleEventMap["tr-submit-tracking"]) => {
	const target = ev.target as HTMLElement;

	const featureOrder = getElementPositionInPageContext(target, "feature-order");

	if (ev.detail.method === "submitMerge") {
		const features = ev.detail.parameterMap.features;
		addPositionToFeatures(featureOrder, features);
	}
	if (ev.detail.method === "submitMove") {
		const features = ev.detail.parameterMap.action?.features;
		addPositionToFeatures(featureOrder, features);
	}
	if (ev.detail.method === "submitEvent") {
		const features = ev.detail.parameterMap.action?.features;
		addPositionToFeatures(featureOrder, features);
	}
};

const addWidgetHash = (ev: DataTrLifeCycleEventMap["tr-submit-tracking"]) => {
	const target = ev.target as HTMLElement;
	const attribute = "feature-tracking-labels";

	const widgetHashContainer = getClosestParentByDataAttribute(target, attribute);
	if (!widgetHashContainer) return;
	const widgetHash = widgetHashContainer.getAttribute(`data-${attribute}`)!;
	const parsedWidgetHash = JSON.parse(removeQuoteEncoding(widgetHash));

	const dealOfTheWeekElement = document.querySelector<HTMLElement>("#products_cinema_deal_of_the_week");
	const dealOfTheMonthElement = document.querySelector<HTMLElement>("#products_cinema_deal_of_the_month");
	const parsedDealOfTheWeek = JSON.parse(dealOfTheWeekElement!.dataset.featureTrackingLabels!);
	const parsedDealOfTheMonth = JSON.parse(dealOfTheMonthElement!.dataset.featureTrackingLabels!);
	const dealOfTheWeekLabels = { ...parsedDealOfTheWeek, ...parsedWidgetHash };
	const dealOfTheMonthLabels = { ...parsedDealOfTheMonth, ...parsedWidgetHash };
	dealOfTheWeekElement!.dataset.featureTrackingLabels = JSON.stringify(dealOfTheWeekLabels);
	dealOfTheMonthElement!.dataset.featureTrackingLabels = JSON.stringify(dealOfTheMonthLabels);

	if (ev.detail.method === "submitMerge") {
		const features = ev.detail.parameterMap.features;
		addLabelsToFeatures(parsedWidgetHash, features);
	}
	if (ev.detail.method === "submitMove") {
		const features = ev.detail.parameterMap.action?.features;
		addLabelsToFeatures(parsedWidgetHash, features);
	}
	if (ev.detail.method === "submitEvent") {
		const features = ev.detail.parameterMap.action?.features;
		addLabelsToFeatures(parsedWidgetHash, features);
	}
};

const addTrackingInformation = (ev: DataTrLifeCycleEventMap["tr-submit-tracking"]) => {
	addFeatureOrder(ev);
	addWidgetHash(ev);
};

const addNextPITrackingLabels = (ev: DataTrLifeCycleEventMap["tr-submit-tracking"]) => {
	const target = ev.target as HTMLElement;
	const attribute = "next-pi-tracking-labels";

	const piLabelContainer = getClosestParentByDataAttribute(target, attribute);
	if (!piLabelContainer) return;
	const piLabel = piLabelContainer.getAttribute(`data-${attribute}`)!;
	const parsedPILabels = JSON.parse(removeQuoteEncoding(piLabel));

	if (ev.detail.method === "submitMove") {
		const features = ev.detail.parameterMap.action?.features;
		addLabelsToFeatures(parsedPILabels, features);
	}
};

const addFeatureTrackingLabels = (ev: DataTrLifeCycleEventMap["tr-submit-tracking"]) => {
	const target = ev.target as HTMLElement;
	const attribute = "feature-tracking-labels";

	const featureLabelContainer = getClosestParentByDataAttribute(target, attribute);
	if (!featureLabelContainer) return;
	const featureLabels = featureLabelContainer.getAttribute(`data-${attribute}`)!;
	const parsedFeatureLabels = JSON.parse(removeQuoteEncoding(featureLabels));

	if (ev.detail.method === "submitMove") {
		const features = ev.detail.parameterMap.action?.features;
		addLabelsToFeatures(parsedFeatureLabels, features);
	}
	if (ev.detail.method === "submitMerge") {
		const features = ev.detail.parameterMap.features;
		addLabelsToFeatures(parsedFeatureLabels, features);
	}
	if (ev.detail.method === "submitEvent") {
		const features = ev.detail.parameterMap.action?.features;
		addLabelsToFeatures(parsedFeatureLabels, features);
	}
};

const addLabelsToFeatures = (featureLabels: DataContainer, features?: Feature[]) => {
	if (!features?.length) return;

	const labels = features[0]!.labels;
	features[0]!.labels = { ...labels, ...featureLabels };
};

const addFT3TrackingInformation = (ev: DataTrLifeCycleEventMap["tr-submit-tracking"]) => {
	addNextPITrackingLabels(ev);
	addFeatureTrackingLabels(ev);
};

delegate(window.document, "tr-submit-tracking", ".ft9_product-highlight__container", addFeatureOrder);
delegate(window.document, "tr-submit-tracking", ".product_highlight_dealblock_container", addTrackingInformation);
delegate(window.document, "tr-submit-tracking", ".ft9_product-highlight__container", addFT3TrackingInformation);
unlockInit("ft9.daily_deal.lock.token");

const dealblockContainer = document.querySelector<HTMLDivElement>("#product-highlight-dealblock");
dealblockContainer && new DealBlockNavigation(dealblockContainer).init();
